// skeleton/app/src/components/ProviderProfile/GraduateScoring/GraduateScoringComponent.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import './GraduateScoringComponent.scss';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { sanitize } from '../../../services/renderingService';
import { ApiEndpoints } from '../../../constants/appConstants';
import httpService from '../../../services/httpService';
import { GraduatedReview } from './GraduatedReviewModels';
import { useAppContext } from '../../../Context';

export interface GraduateJustificationProps {
    header: string;
    message: string;
    style?: React.CSSProperties;
    visible: boolean;
    onHide: () => void;
    isReadOnly: boolean;
    reasons: any[];
    selectedReasonIds: any[];
    userReason?: string;
    onReasonChange: (e: any) => void;
    onSave?: () => void;
    loading?: boolean;
    graduatedMeasures?: any[];
    mpiId?: string;
    graduatedReview?: GraduatedReview[];
    reviewYear?: number;
}

type UIMode = 'FAIL_REASONS' | 'SCORING_CRITERIA';

interface ApprovalOption {
    label: string;
    value: string;
}

const APPROVAL_STATUS = {
    APPROVED: 'A',
    NOT_APPROVED: 'NA'
} as const;

const APPROVAL_OPTIONS: ApprovalOption[] = [
    {
        label: 'Approved',
        value: APPROVAL_STATUS.APPROVED
    },
    {
        label: 'Not Approved',
        value: APPROVAL_STATUS.NOT_APPROVED
    }
];

export default function GraduateJustificationSideBar({
    header,
    message,
    style,
    visible,
    onHide,
    isReadOnly,
    reasons,
    selectedReasonIds,
    onReasonChange,
    onSave,
    graduatedMeasures,
    mpiId,
    graduatedReview,
    reviewYear: propReviewYear
}: GraduateJustificationProps) {
    const [uiMode, setUiMode] = useState<UIMode>('FAIL_REASONS');

    const [currentGraduateIndex, setCurrentGraduateIndex] =
        useState<number>(-1);

    const [selectedApprovalStatus, setSelectedApprovalStatus] =
        useState<string | null>(null);

    const [localSelectedReasons, setLocalSelectedReasons] =
        useState<any[]>([]);

    const [hasProcessedExternalReasons, setHasProcessedExternalReasons] =
        useState<boolean>(false);

    const {
        reviewYear: contextReviewYear
    } = useAppContext();

    const reviewYear =
        propReviewYear ?? contextReviewYear;

    const lastLoadedMeasureRef =
        React.useRef<number>(-1);

    const [accumulatedData, setAccumulatedData] = useState<{
        externalReasons: any[];
        graduateMeasures: Array<{
            reviewResponseId?: number | null;
            measureId: string;
            measureNumber: string;
            tierId: string;
            tierCode: string;
            approvalStatus: string;
            selectedReasons: any[];
        }>;
    }>({
        externalReasons: [],
        graduateMeasures: []
    });

    // Enrich graduated measures with existing review data
    // and sort them by target tier level.
    //
    // IMPORTANT:
    // Clone the incoming array before sorting so props are not mutated.
    const sortedGraduatedMeasures = React.useMemo(() => {
        if (!graduatedMeasures || graduatedMeasures.length === 0) {
            return [];
        }

        if (
            graduatedReview &&
            Array.isArray(graduatedReview)
        ) {
            const enrichedMeasures = graduatedMeasures.map((measure) => {
                const reviewData = graduatedReview.find(
                    (review: any) =>
                        String(review.graduateMeasureId) ===
                            String(measure.targetMeasureId) &&
                        String(review.graduateTierId) ===
                            String(measure.targetTierId)
                );

                if (reviewData) {
                    const selectedReasons =
                        reviewData.reviewDetails?.reasons || [];

                    return {
                        ...measure,
                        reviewResponseId:
                            reviewData.reviewResponseId,
                        approvalStatus:
                            reviewData.reviewResponse,
                        selectedReasons: selectedReasons.map(
                            (reason: any) => ({
                                reasonId: reason.reasonId,
                                reasonText: reason.reasonText,
                                reasonCode: reason.reasonCode || ''
                            })
                        )
                    };
                }

                return {
                    ...measure,
                    reviewResponseId: null,
                    approvalStatus: null,
                    selectedReasons: []
                };
            });

            return [...enrichedMeasures].sort((a, b) => {
                const tierA = a.targetTierLevel || 0;
                const tierB = b.targetTierLevel || 0;

                return tierA - tierB;
            });
        }

        return [...graduatedMeasures].sort((a, b) => {
            const tierA = a.targetTierLevel || 0;
            const tierB = b.targetTierLevel || 0;

            return tierA - tierB;
        });
    }, [
        graduatedMeasures,
        graduatedReview
    ]);

    // Reset the sidebar workflow each time it opens.
    useEffect(() => {
        if (visible) {
            setUiMode('FAIL_REASONS');
            setCurrentGraduateIndex(-1);
            setHasProcessedExternalReasons(false);
            setSelectedApprovalStatus(null);
            setLocalSelectedReasons([]);
            setAccumulatedData({
                externalReasons: [],
                graduateMeasures: []
            });
            lastLoadedMeasureRef.current = -1;
        }
    }, [visible]);

    // Load existing approval/reason selections for the currently
    // selected graduate measure.
    useEffect(() => {
        if (
            uiMode === 'SCORING_CRITERIA' &&
            currentGraduateIndex >= 0 &&
            currentGraduateIndex < sortedGraduatedMeasures.length
        ) {
            if (
                lastLoadedMeasureRef.current !== currentGraduateIndex
            ) {
                lastLoadedMeasureRef.current =
                    currentGraduateIndex;

                const currentMeasure =
                    sortedGraduatedMeasures[currentGraduateIndex];

                if (currentMeasure) {
                    setSelectedApprovalStatus(
                        currentMeasure.approvalStatus || null
                    );

                    if (
                        currentMeasure.selectedReasons &&
                        currentMeasure.selectedReasons.length > 0
                    ) {
                        setLocalSelectedReasons(
                            currentMeasure.selectedReasons
                        );
                    } else {
                        setLocalSelectedReasons([]);
                    }
                }
            }
        }
    }, [
        uiMode,
        currentGraduateIndex,
        sortedGraduatedMeasures
    ]);

    const handleClose = useCallback(() => {
        setUiMode('FAIL_REASONS');
        setCurrentGraduateIndex(-1);
        setSelectedApprovalStatus(null);
        setLocalSelectedReasons([]);
        setHasProcessedExternalReasons(false);

        setAccumulatedData({
            externalReasons: [],
            graduateMeasures: []
        });

        lastLoadedMeasureRef.current = -1;

        onHide();
    }, [onHide]);

    const buildMeasureData = useCallback(
        (
            measure: any,
            approvalStatus: string,
            selectedReasonsForMeasure: any[]
        ) => ({
            reviewResponseId:
                measure?.reviewResponseId,

            measureId:
                measure?.targetMeasureId,

            measureNumber:
                measure?.targetMeasureNumber,

            tierId:
                measure?.targetTierId,

            tierCode:
                measure?.targetTierCode,

            tierLevel:
                measure?.targetTierLevel,

            approvalStatus,

            selectedReasons:
                selectedReasonsForMeasure || []
        }),
        []
    );

    const accumulateMeasureData = useCallback(
        (
            approvalStatus: string,
            selectedReasonsForMeasure: any[]
        ) => {
            if (
                currentGraduateIndex < 0 ||
                currentGraduateIndex >= sortedGraduatedMeasures.length
            ) {
                return;
            }

            const currentMeasure =
                sortedGraduatedMeasures[currentGraduateIndex];

            const measureData = buildMeasureData(
                currentMeasure,
                approvalStatus,
                selectedReasonsForMeasure
            );

            setAccumulatedData((prev) => ({
                ...prev,
                graduateMeasures: [
                    ...prev.graduateMeasures,
                    measureData
                ]
            }));
        },
        [
            sortedGraduatedMeasures,
            currentGraduateIndex,
            buildMeasureData
        ]
    );

    const navigateToNextMeasure = useCallback(() => {
        const nextIndex =
            currentGraduateIndex + 1;

        if (
            nextIndex < sortedGraduatedMeasures.length
        ) {
            setCurrentGraduateIndex(nextIndex);
            setSelectedApprovalStatus(null);
            setLocalSelectedReasons([]);
        } else {
            handleClose();
        }
    }, [
        sortedGraduatedMeasures,
        currentGraduateIndex,
        handleClose
    ]);

    const saveGraduaateMeasureReview = useCallback(
        async (finalData: any) => {
            try {
                if (
                    finalData?.graduateMeasures &&
                    Array.isArray(finalData.graduateMeasures) &&
                    finalData.graduateMeasures.length > 0
                ) {
                    const graduateReviews =
                        finalData.graduateMeasures.map(
                            (measure: any) => ({
                                reviewResponseId:
                                    measure?.reviewResponseId || null,

                                tierId:
                                    measure?.tierId,

                                tierLevel:
                                    measure?.tierLevel,

                                mpiId,

                                measureId:
                                    measure?.measureId,

                                reviewResponse:
                                    measure?.approvalStatus,

                                isReviewed: true,

                                reasons: (
                                    Array.isArray(
                                        measure?.selectedReasons
                                    )
                                        ? measure.selectedReasons
                                        : []
                                ).map((reason: any) => ({
                                    reasonId:
                                        reason.reasonId,

                                    reasonText:
                                        reason.reasonText
                                }))
                            })
                        );

                    /*
                     * currentGraduateIndex follows sortedGraduatedMeasures.
                     * Use that same array when resolving the source fields.
                     * Falling back to the first measure is safe because all
                     * graduated records in this workflow belong to the same
                     * source measure/tier.
                     */
                    const currentGraduateMeasure =
                        sortedGraduatedMeasures[
                            currentGraduateIndex
                        ] ||
                        sortedGraduatedMeasures[0];

                    const payload = {
                        providerMpi:
                            mpiId,

                        ReviewYear:
                            reviewYear,

                        sourceTierId:
                            currentGraduateMeasure?.sourceTierId ??
                            null,

                        sourceMeasureId:
                            currentGraduateMeasure?.sourceMeasureId ??
                            null,

                        ReviewResponses:
                            graduateReviews
                    };

                    await httpService.post(
                        ApiEndpoints.saveGraduaateMeasureReview,
                        payload
                    );
                }

                if (onSave) {
                    onSave();
                }

                handleClose();
            } catch (error) {
                console.error(
                    '[GraduateJustificationSideBar] Error saving graduated measure review:',
                    error
                );
            }
        },
        [
            mpiId,
            reviewYear,
            sortedGraduatedMeasures,
            currentGraduateIndex,
            onSave,
            handleClose
        ]
    );

    const handleSave = useCallback(async () => {
        /*
         * Original measure has failure reasons but there are
         * no lower-tier graduated measures to score.
         */
        if (
            uiMode === 'FAIL_REASONS' &&
            !hasProcessedExternalReasons
        ) {
            if (
                sortedGraduatedMeasures.length === 0
            ) {
                if (onSave) {
                    onSave();
                }

                handleClose();
            }

            return;
        }

        /*
         * Current graduated measure is already in its
         * failure-reason step.
         */
        if (
            uiMode === 'FAIL_REASONS' &&
            hasProcessedExternalReasons
        ) {
            if (
                currentGraduateIndex < 0 ||
                currentGraduateIndex >= sortedGraduatedMeasures.length
            ) {
                return;
            }

            const currentMeasure =
                sortedGraduatedMeasures[currentGraduateIndex];

            const measureData = buildMeasureData(
                currentMeasure,
                APPROVAL_STATUS.NOT_APPROVED,
                localSelectedReasons || []
            );

            const finalData = {
                externalReasons:
                    accumulatedData.externalReasons,

                graduateMeasures: [
                    ...accumulatedData.graduateMeasures,
                    measureData
                ]
            };

            await saveGraduaateMeasureReview(
                finalData
            );

            return;
        }

        /*
         * Current graduated measure is being scored
         * Approved / Not Approved.
         */
        if (
            uiMode === 'SCORING_CRITERIA' &&
            currentGraduateIndex >= 0 &&
            currentGraduateIndex < sortedGraduatedMeasures.length
        ) {
            const currentMeasure =
                sortedGraduatedMeasures[currentGraduateIndex];

            const selectedReasonsForMeasure =
                selectedApprovalStatus ===
                APPROVAL_STATUS.NOT_APPROVED
                    ? localSelectedReasons || []
                    : [];

            const measureData = buildMeasureData(
                currentMeasure,
                selectedApprovalStatus || '',
                selectedReasonsForMeasure
            );

            const finalData = {
                externalReasons:
                    accumulatedData.externalReasons,

                graduateMeasures: [
                    ...accumulatedData.graduateMeasures,
                    measureData
                ]
            };

            await saveGraduaateMeasureReview(
                finalData
            );
        }
    }, [
        uiMode,
        hasProcessedExternalReasons,
        sortedGraduatedMeasures,
        currentGraduateIndex,
        localSelectedReasons,
        accumulatedData,
        selectedApprovalStatus,
        buildMeasureData,
        saveGraduaateMeasureReview,
        onSave,
        handleClose
    ]);

    const handleNext = useCallback(async () => {
        if (
            uiMode === 'FAIL_REASONS'
        ) {
            /*
             * First step:
             * capture the original/source measure reasons and
             * start graduate scoring.
             */
            if (
                !hasProcessedExternalReasons
            ) {
                setAccumulatedData((prev) => ({
                    ...prev,
                    externalReasons:
                        selectedReasonIds
                }));

                if (
                    sortedGraduatedMeasures.length > 0
                ) {
                    setHasProcessedExternalReasons(
                        true
                    );

                    setCurrentGraduateIndex(
                        0
                    );

                    setUiMode(
                        'SCORING_CRITERIA'
                    );
                } else {
                    handleClose();
                }

                return;
            }

            /*
             * Current graduated measure was marked NA and
             * its failure reasons have now been entered.
             */
            accumulateMeasureData(
                APPROVAL_STATUS.NOT_APPROVED,
                localSelectedReasons || []
            );

            setUiMode(
                'SCORING_CRITERIA'
            );

            navigateToNextMeasure();

            return;
        }

        if (
            uiMode === 'SCORING_CRITERIA'
        ) {
            const selectedReasonsForMeasure =
                selectedApprovalStatus ===
                APPROVAL_STATUS.NOT_APPROVED
                    ? localSelectedReasons || []
                    : [];

            accumulateMeasureData(
                selectedApprovalStatus || '',
                selectedReasonsForMeasure
            );

            navigateToNextMeasure();
        }
    }, [
        uiMode,
        hasProcessedExternalReasons,
        selectedReasonIds,
        sortedGraduatedMeasures,
        localSelectedReasons,
        selectedApprovalStatus,
        accumulateMeasureData,
        navigateToNextMeasure,
        handleClose
    ]);

    const handleLocalReasonChange = useCallback(
        (
            e: any,
            reason: any
        ) => {
            if (!reason) {
                return;
            }

            setLocalSelectedReasons((prev) => {
                const currentReasons =
                    prev || [];

                if (e?.checked) {
                    const exists =
                        currentReasons.some(
                            (currentReason: any) =>
                                currentReason?.reasonId ===
                                reason.reasonId
                        );

                    if (exists) {
                        return currentReasons;
                    }

                    return [
                        ...currentReasons,
                        {
                            reasonId:
                                reason.reasonId,

                            reasonText:
                                reason.reasonText
                        }
                    ];
                }

                return currentReasons.filter(
                    (currentReason: any) =>
                        currentReason?.reasonId !==
                        reason.reasonId
                );
            });
        },
        []
    );

    const renderHeader = useCallback(
        (title: string) => (
            <div className="comments-sidebar-header">
                <h3>
                    {title}
                </h3>

                <Button
                    onClick={handleClose}
                    icon="pi pi-times"
                    size="small"
                    rounded
                    outlined
                    severity="secondary"
                    aria-label="Cancel"
                />
            </div>
        ),
        [handleClose]
    );

    const renderCheckboxList = useCallback(
        (
            reasonOptions: any[],
            selectedIds: any[],
            onChange: (
                e: any,
                reason: any
            ) => void,
            idPrefix: string,
            isScoring: boolean = false
        ) =>
            reasonOptions.length === 0 ? (
                <div className="no-reasons-message">
                    No failed reasons available for this measure.
                </div>
            ) : (
                reasonOptions.map(
                    (reason: any) =>
                        reason && (
                            <div
                                key={
                                    reason.reasonId
                                }
                                className={`flex align-items-center reason-item ${
                                    isScoring
                                        ? 'scoring-criteria'
                                        : ''
                                }`}
                            >
                                <Checkbox
                                    inputId={
                                        idPrefix +
                                        (
                                            reason.reasonId ||
                                            ''
                                        )
                                    }
                                    name={
                                        idPrefix +
                                        (
                                            reason.reasonText ||
                                            ''
                                        )
                                    }
                                    value={
                                        reason.reasonId
                                    }
                                    checked={
                                        selectedIds.includes(
                                            reason.reasonId
                                        )
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        onChange(
                                            e,
                                            reason
                                        )
                                    }
                                    disabled={
                                        isReadOnly
                                    }
                                />

                                <label
                                    htmlFor={
                                        idPrefix +
                                        (
                                            reason.reasonId ||
                                            ''
                                        )
                                    }
                                >
                                    {reason.reasonText ||
                                        ''}
                                </label>
                            </div>
                        )
                )
            ),
        [isReadOnly]
    );

    const getCurrentMeasure =
        useCallback(() => {
            if (
                currentGraduateIndex >=
                    0 &&
                currentGraduateIndex <
                    sortedGraduatedMeasures.length
            ) {
                return sortedGraduatedMeasures[
                    currentGraduateIndex
                ];
            }

            return null;
        }, [
            currentGraduateIndex,
            sortedGraduatedMeasures
        ]);

    const shouldShowNextButton =
        useCallback(() => {
            if (
                currentGraduateIndex >=
                sortedGraduatedMeasures.length -
                    1
            ) {
                return false;
            }

            if (
                uiMode ===
                    'FAIL_REASONS' &&
                !hasProcessedExternalReasons
            ) {
                return (
                    sortedGraduatedMeasures.length >
                    0
                );
            }

            if (
                uiMode ===
                    'FAIL_REASONS' &&
                hasProcessedExternalReasons
            ) {
                return true;
            }

            return (
                uiMode ===
                    'SCORING_CRITERIA' &&
                selectedApprovalStatus ===
                    APPROVAL_STATUS.NOT_APPROVED
            );
        }, [
            sortedGraduatedMeasures,
            currentGraduateIndex,
            uiMode,
            hasProcessedExternalReasons,
            selectedApprovalStatus
        ]);

    const isButtonDisabled =
        useCallback(() => {
            if (
                uiMode ===
                'FAIL_REASONS'
            ) {
                return hasProcessedExternalReasons
                    ? localSelectedReasons.length ===
                          0
                    : !selectedReasonIds ||
                          selectedReasonIds.length ===
                              0;
            }

            if (
                uiMode ===
                'SCORING_CRITERIA'
            ) {
                if (
                    !selectedApprovalStatus
                ) {
                    return true;
                }

                return (
                    selectedApprovalStatus ===
                        APPROVAL_STATUS.NOT_APPROVED &&
                    localSelectedReasons.length ===
                        0
                );
            }

            return true;
        }, [
            uiMode,
            hasProcessedExternalReasons,
            localSelectedReasons,
            selectedReasonIds,
            selectedApprovalStatus
        ]);

    const renderFailReasonsUI =
        useCallback(() => {
            const currentMeasure =
                getCurrentMeasure();

            const displayReasons =
                hasProcessedExternalReasons
                    ? currentMeasure?.targetFailureReasons ||
                      []
                    : reasons || [];

            const displaySelectedIds =
                hasProcessedExternalReasons
                    ? localSelectedReasons
                          .map(
                              (
                                  reason: any
                              ) =>
                                  reason?.reasonId
                          )
                          .filter(
                              (
                                  id
                              ) =>
                                  id !==
                                  undefined
                          )
                    : [
                          ...(
                              selectedReasonIds ||
                              []
                          )
                      ].filter(
                          (
                              id,
                              index,
                              self
                          ) =>
                              self.indexOf(
                                  id
                              ) ===
                              index
                      );

            const title =
                hasProcessedExternalReasons &&
                currentMeasure?.targetMeasureNumber
                    ? `Graduate Scoring Measure: ${currentMeasure.targetMeasureNumber} - Select fail reasons`
                    : header;

            const onChange =
                hasProcessedExternalReasons
                    ? handleLocalReasonChange
                    : onReasonChange;

            return (
                <>
                    {renderHeader(
                        title
                    )}

                    <div className="comments-sidebar-content">
                        <div className="intro-section">
                            <p className="justification-message">
                                {message}
                            </p>
                        </div>

                        {renderCheckboxList(
                            displayReasons,
                            displaySelectedIds,
                            onChange,
                            'chkReason',
                            false
                        )}

                        <div className="spacer" />
                    </div>
                </>
            );
        }, [
            getCurrentMeasure,
            hasProcessedExternalReasons,
            reasons,
            localSelectedReasons,
            selectedReasonIds,
            header,
            message,
            handleLocalReasonChange,
            onReasonChange,
            renderHeader,
            renderCheckboxList
        ]);

    const renderScoringCriteriaUI =
        useCallback(() => {
            const currentMeasure =
                getCurrentMeasure();

            const title =
                currentMeasure?.targetMeasureNumber
                    ? `Graduate Measure-${currentMeasure.targetMeasureNumber}`
                    : 'Graduate Measure';

            const failureReasons =
                currentMeasure?.targetFailureReasons ||
                [];

            const selectedIds =
                localSelectedReasons
                    .map(
                        (
                            reason: any
                        ) =>
                            reason?.reasonId
                    )
                    .filter(
                        (
                            id
                        ) =>
                            id !==
                            undefined
                    );

            return (
                <>
                    {renderHeader(
                        title
                    )}

                    <div className="comments-sidebar-content">
                        <div className="measure-info-section">
                            <p className="tier-label">
                                Please score this
                                measure against the
                                following tier:{' '}

                                <span className="tier-name">
                                    {currentMeasure?.targetTierName ||
                                        'N/A'}
                                </span>
                            </p>

                            <p className="guidance-label">
                                Scoring Guidance:
                            </p>

                            {currentMeasure?.targetScoringGuidance ? (
                                <div
                                    className="guidance-content"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            sanitize(
                                                currentMeasure.targetScoringGuidance
                                            )
                                    }}
                                />
                            ) : (
                                <div className="guidance-empty" />
                            )}
                        </div>

                        <div className="approval-section">
                            <label>
                                Select Approval
                                Status:
                            </label>

                            <Dropdown
                                value={
                                    selectedApprovalStatus
                                }
                                options={
                                    APPROVAL_OPTIONS
                                }
                                onChange={(
                                    e
                                ) => {
                                    setSelectedApprovalStatus(
                                        e.value
                                    );

                                    /*
                                     * Approved does not carry
                                     * failure reasons.
                                     */
                                    if (
                                        e.value ===
                                        APPROVAL_STATUS.APPROVED
                                    ) {
                                        setLocalSelectedReasons(
                                            []
                                        );
                                    }
                                }}
                                placeholder="Select Status"
                                disabled={
                                    isReadOnly
                                }
                                appendTo="self"
                            />
                        </div>

                        {selectedApprovalStatus ===
                            APPROVAL_STATUS.NOT_APPROVED && (
                            <div className="failure-reasons-section">
                                <label className="section-label">
                                    Select
                                    Failure
                                    Reason(s):
                                </label>

                                <div className="reasons-container">
                                    {renderCheckboxList(
                                        failureReasons,
                                        selectedIds,
                                        handleLocalReasonChange,
                                        'chkGradReason',
                                        true
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="spacer" />
                    </div>
                </>
            );
        }, [
            getCurrentMeasure,
            selectedApprovalStatus,
            isReadOnly,
            localSelectedReasons,
            handleLocalReasonChange,
            renderHeader,
            renderCheckboxList
        ]);

    return (
        <Sidebar
            position="right"
            visible={visible}
            style={style}
            onHide={handleClose}
            className="justification-sidebar"
            showCloseIcon={false}
        >
            {uiMode ===
            'FAIL_REASONS'
                ? renderFailReasonsUI()
                : renderScoringCriteriaUI()}

            <div className="comments-sidebar-footer">
                {shouldShowNextButton() ? (
                    <Button
                        onClick={
                            handleNext
                        }
                        disabled={
                            isReadOnly ||
                            isButtonDisabled()
                        }
                        className="justification-save-btn"
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        onClick={
                            handleSave
                        }
                        disabled={
                            isReadOnly ||
                            isButtonDisabled()
                        }
                        className="justification-save-btn"
                    >
                        Save
                    </Button>
                )}
            </div>
        </Sidebar>
    );
}